/* Data layer for the attendance grid: status config, date window (with
   batch auto-snap), grid loading, and the optimistic local-update math. */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../../hooks/useApi';
import { FALLBACK_STATUSES, todayIso, addDays } from './attendanceUtils';
import { ALL } from '../TraineeFilterContext';

export function useAttendanceData({ batchId, scopeQS }) {
  const { get } = useApi();

  const [allStatuses, setAllStatuses] = useState(FALLBACK_STATUSES);
  const activeStatuses = useMemo(() => allStatuses.filter(s => s.is_active !== false), [allStatuses]);
  const statusMap = useMemo(
    () => Object.fromEntries(allStatuses.map(s => [s.code, s])),
    [allStatuses],
  );

  // Default window: today − 13 → today (14 days)
  const [endDate, setEndDate] = useState(todayIso());
  const [startDate, setStartDate] = useState(addDays(todayIso(), -13));
  const [data, setData] = useState({ dates: [], rows: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Include inactive so historical marks whose status was deactivated
        // later still resolve in the cell renderer.
        const res = await get('/trainees/attendance-statuses?include_inactive=true');
        if (res.statuses?.length) setAllStatuses(res.statuses);
      } catch { /* keep fallback */ }
    })();
  }, [get]);

  // Auto-snap the date window when a batch is picked (historical batches
  // default to their training start/end, not "today − 13").
  useEffect(() => {
    if (batchId === ALL) return;
    (async () => {
      try {
        const b = await get(`/trainees/batches/${batchId}`);
        const sd = (b.start_date || '').slice(0, 10);
        const ed = (b.end_date || '').slice(0, 10);
        if (!sd) return;
        const today = todayIso();
        const status = (b.status || 'active').toLowerCase();
        let nextEnd;
        if (status === 'active') {
          nextEnd = ed && ed < today ? ed : today;
        } else {
          nextEnd = ed || today;
        }
        if (nextEnd < sd) nextEnd = sd;
        setStartDate(sd);
        setEndDate(nextEnd);
      } catch { /* keep current window */ }
    })();
  }, [batchId, get]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      const scope = scopeQS();
      if (scope) { const [k, v] = scope.split('='); p.append(k, decodeURIComponent(v)); }
      p.append('start_date', startDate);
      p.append('end_date', endDate);
      const res = await get(`/trainees/attendance/grid?${p.toString()}`);
      setData({ dates: res.dates || [], rows: res.rows || [] });
    } catch {
      toast.error('Failed to load attendance grid');
    } finally {
      setLoading(false);
    }
  }, [get, scopeQS, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const shiftWindow = (days) => {
    setStartDate(addDays(startDate, days));
    setEndDate(addDays(endDate, days));
  };

  const setWindowDays = (n) => {
    const end = todayIso();
    setEndDate(end);
    setStartDate(addDays(end, -(n - 1)));
  };

  // ── Optimistic local-update math (mirrors backend _status_weights) ──
  const weightsFor = useCallback((code) => {
    if (!code) return [0, 0];
    const s = statusMap[code];
    if (!s) return [0, 0];
    const wd = s.counts_as_working_day ? 1 : 0;
    const ps = (s.counts_as_working_day && s.counts_as_present)
      ? Math.max(0, Math.min(1, Number(s.attendance_weight ?? 1)))
      : 0;
    return [wd, ps];
  }, [statusMap]);

  const recomputeRowTotals = useCallback((row) => {
    let wd = 0;
    let ps = 0;
    for (const c of row.cells) {
      const [w, p] = weightsFor(c.status);
      wd += w;
      ps += p;
    }
    const pct = wd > 0 ? Math.round((ps / wd) * 10000) / 100 : 0;
    return { total_working_days: wd, total_attendance: ps, attendance_pct: pct };
  }, [weightsFor]);

  // Apply status changes to N cells in local state (status=null clears —
  // and drops the cell's remark, matching the server).
  const applyLocalChanges = useCallback((changes) => {
    setData(prev => {
      const byTrainee = new Map();
      for (const ch of changes) {
        if (!byTrainee.has(ch.trainee_id)) byTrainee.set(ch.trainee_id, new Map());
        byTrainee.get(ch.trainee_id).set(ch.date, ch.status);
      }
      return {
        ...prev,
        rows: prev.rows.map(r => {
          const updates = byTrainee.get(r.trainee_id);
          if (!updates) return r;
          const newRow = {
            ...r,
            cells: r.cells.map(c => updates.has(c.date)
              ? { ...c, status: updates.get(c.date),
                  ...(updates.get(c.date) === null ? { remark: null } : {}) }
              : c),
          };
          return { ...newRow, ...recomputeRowTotals(newRow) };
        }),
      };
    });
  }, [recomputeRowTotals]);

  const setCellRemark = useCallback((traineeId, date, remark) => {
    setData(prev => ({
      ...prev,
      rows: prev.rows.map(r => r.trainee_id !== traineeId ? r : {
        ...r,
        cells: r.cells.map(c => c.date === date ? { ...c, remark } : c),
      }),
    }));
  }, []);

  return {
    allStatuses, activeStatuses, statusMap,
    startDate, endDate, setStartDate, setEndDate, shiftWindow, setWindowDays,
    data, setData, loading, load,
    applyLocalChanges, setCellRemark,
  };
}
